import { PhoneFieldInput } from '@/components/forms/PhoneFieldInput'
import { DateInput } from '@/components/ui/date-input'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import type { FileUploadConfig } from '@/types/api/file'
import type { FormField } from '@/types/api/form'
import { FormFieldType } from '@/types/enums'
import { cn } from '@/utils/cn'
import { getFieldInputType } from '@/utils/field-formatters'

import { ratingStars } from '../utils/layoutField'
import { fieldChoices } from '../utils/recordFilters'

import { EmailMessageFieldInput } from './EmailMessageFieldInput'
import { FileFieldInput, type FileUploadFn } from './FileFieldInput'

export interface RecordFieldInputProps {
  field: FormField
  value: string
  /** Validation message; the caller renders it (with `${id}-error`) and the input points at it. */
  error: string | null
  onChange: (value: string) => void
  disabled?: boolean
  /** Element id; defaults to the field id. */
  id?: string
  /** Uploads a file of a FILE_UPLOAD field; without it such a field shows a read-only placeholder. */
  uploadFile?: FileUploadFn
  /** Global upload policy for FILE_UPLOAD fields. */
  fileConfig?: Pick<FileUploadConfig, 'maxFileSizeBytes' | 'allowedTypes'> | null
  /** Told when a file dialog opens or closes. */
  onUploadingChange?: (uploading: boolean) => void
  /** The record's values by field key; a linked-email field fills its default search from them. */
  valuesByKey?: Record<string, string>
}

/** Choices of a RADIO or CHECKBOX field. */
export function choiceOptions(field: FormField): string[] {
  return fieldChoices(field)
}

/** CHECKBOX fields hold several values (a JSON array) unless their selection mode is SINGLE. */
export function isMultiChoice(field: FormField): boolean {
  const options = (field.options ?? {}) as Record<string, unknown>
  return field.type === FormFieldType.CHECKBOX && String(options.selectionMode ?? 'MULTIPLE').toUpperCase() !== 'SINGLE'
}

function ChoiceInput({
  field, value, onChange, disabled, id, invalid, describedBy,
}: {
  field: FormField
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  id: string
  invalid: boolean
  describedBy?: string
}) {
  const choices = choiceOptions(field)
  const multiple = isMultiChoice(field)
  let selected: string[] = []
  if (multiple) {
    try {
      selected = JSON.parse(value || '[]')
    } catch {
      selected = []
    }
  }
  return (
    <div
      id={id}
      role={multiple ? 'group' : 'radiogroup'}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      aria-label={field.label}
      className="space-y-2"
    >
      {choices.map((choice) => (
        <label key={choice} className="flex items-center gap-2 text-sm">
          <input
            type={multiple ? 'checkbox' : 'radio'}
            name={field.id}
            disabled={disabled}
            checked={(multiple ? selected : [value]).includes(choice)}
            onChange={(e) => {
              if (multiple) {
                onChange(JSON.stringify(e.target.checked ? [...selected, choice] : selected.filter((v) => v !== choice)))
              } else {
                onChange(choice)
              }
            }}
          />
          {choice}
        </label>
      ))}
    </div>
  )
}

export function RatingInput({
  field, value, onChange, disabled, id, invalid, describedBy,
}: {
  field: Pick<FormField, 'label' | 'options'>
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  id: string
  invalid: boolean
  describedBy?: string
}) {
  const stars = ratingStars(field)
  const current = Number(value) || 0
  return (
    <div id={id} role="radiogroup" aria-label={field.label} aria-invalid={invalid || undefined} aria-describedby={describedBy} className="flex items-center gap-0.5">
      {Array.from({ length: stars }, (_, i) => i + 1).map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={current === n}
          aria-label={`${n} of ${stars} stars`}
          disabled={disabled}
          // clicking the chosen star again clears the rating
          onClick={() => onChange(current === n ? '' : String(n))}
          className={cn('rounded p-0.5 text-2xl leading-none transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400',
            n <= current ? 'text-amber-400' : 'text-neutral-300 hover:text-amber-300')}
        >
          ★
        </button>
      ))}
    </div>
  )
}

/** Input for one form field, by field type. */
export function RecordFieldInput({
  field, value, error, onChange, disabled, id, uploadFile, fileConfig, onUploadingChange, valuesByKey,
}: RecordFieldInputProps) {
  const inputId = id ?? field.id
  const describedBy = error ? `${inputId}-error` : undefined
  const invalid = !!error

  if (field.type === FormFieldType.FILE_UPLOAD) {
    return (
      <FileFieldInput
        id={inputId}
        field={field}
        value={value}
        onChange={onChange}
        upload={uploadFile}
        fileConfig={fileConfig}
        canEdit={!disabled}
        onUploadingChange={onUploadingChange}
      />
    )
  }

  if (field.type === FormFieldType.EMAIL_MESSAGE) {
    return <EmailMessageFieldInput id={inputId} field={field} value={value} onChange={onChange} disabled={disabled} valuesByKey={valuesByKey} />
  }

  if (field.type === FormFieldType.SELECT) {
    const choices = choiceOptions(field)
    return (
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger
          id={inputId}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          className={error ? 'border-destructive-500' : ''}
        >
          <SelectValue placeholder={`Select ${field.label}…`} />
        </SelectTrigger>
        <SelectContent>
          {choices.length === 0 ? (
            <SelectItem value="__no_options__" disabled>
              No options configured
            </SelectItem>
          ) : (
            choices.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
    )
  }

  if (field.type === FormFieldType.RADIO || field.type === FormFieldType.CHECKBOX) {
    return (
      <ChoiceInput
        field={field}
        value={value}
        onChange={onChange}
        disabled={disabled}
        id={inputId}
        invalid={invalid}
        describedBy={describedBy}
      />
    )
  }

  if (field.type === FormFieldType.PARAGRAPH) {
    return (
      <Textarea
        id={inputId}
        value={value}
        rows={4}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onChange={(e) => onChange(e.target.value)}
        placeholder={`Enter ${field.label.toLowerCase()}…`}
        className={cn('border-neutral-300', error && 'border-destructive-500')}
      />
    )
  }

  if (field.type === FormFieldType.RATING) {
    return (
      <RatingInput
        field={field} value={value} onChange={onChange} disabled={disabled}
        id={inputId} invalid={invalid} describedBy={describedBy}
      />
    )
  }

  if (field.type === FormFieldType.TOGGLE) {
    return (
      <label className="flex items-center gap-2 text-sm">
        <input
          id={inputId}
          type="checkbox"
          disabled={disabled}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          checked={value === 'true'}
          onChange={(e) => onChange(String(e.target.checked))}
        />
        Enabled
      </label>
    )
  }

  if (field.type === FormFieldType.PHONE) {
    return (
      <PhoneFieldInput
        id={inputId}
        value={value}
        error={error}
        showError={false}
        describedBy={describedBy}
        options={field.options}
        onChange={onChange}
      />
    )
  }

  if (field.type === FormFieldType.DATE) {
    return (
      <DateInput
        id={inputId}
        value={value}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onChange={onChange}
        className={cn('border-neutral-300', error && 'border-destructive-500')}
      />
    )
  }

  return (
    <div className="relative">
      {field.type === FormFieldType.CURRENCY && (
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 select-none text-sm text-neutral-400">
          ₹
        </span>
      )}
      {field.type === FormFieldType.PERCENT && (
        <span className="pointer-events-none absolute right-8 top-1/2 -translate-y-1/2 select-none text-sm text-neutral-400">
          %
        </span>
      )}
      <Input
        id={inputId}
        type={getFieldInputType(field.type as FormFieldType)}
        value={value}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onChange={(e) => onChange(e.target.value)}
        placeholder={
          field.type === FormFieldType.CURRENCY
            ? '0.00'
            : `Enter ${field.label.toLowerCase()}…`
        }
        className={cn('border-neutral-300', field.type === FormFieldType.CURRENCY && 'pl-7', error && 'border-destructive-500')}
      />
    </div>
  )
}
